import { TranslationKeys } from '../../i18n';
import { isValidPassword } from './utils';

/**
 * Sentinel codes we raise ourselves, shaped like Firebase error codes so
 * `changePasswordErrorKey` has a single thing to look at.
 */
export const NO_FIREBASE_SESSION = 'dashboard/no-firebase-session';
export const NO_PASSWORD_PROVIDER = 'dashboard/no-password-provider';

export class ChangePasswordError extends Error {
    constructor(public readonly code: string) {
        super(code);
        this.name = 'ChangePasswordError';
    }
}

/**
 * Firebase error code → user-facing message.
 *
 * `auth/invalid-credential` is in the wrong-password bucket on purpose: since
 * the Identity Platform rollout, Firebase answers a bad re-auth password with
 * that generic code instead of `auth/wrong-password` (which older projects
 * still return), so both have to mean the same thing here or the most common
 * failure of this form falls through to the generic message.
 *
 * Anything unmapped falls back to the generic error rather than leaking a raw
 * Firebase code into the UI.
 */
export function changePasswordErrorKey(error: unknown): TranslationKeys {
    const code = (error as { code?: unknown } | null)?.code;
    switch (typeof code === 'string' ? code : '') {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
            return 'auth.change_password_wrong_current';
        case 'auth/weak-password':
            return 'auth.change_password_too_short';
        case 'auth/too-many-requests':
            return 'auth.change_password_too_many_requests';
        case 'auth/network-request-failed':
            return 'auth.change_password_network_error';
        case 'auth/requires-recent-login':
        case 'auth/user-token-expired':
        case 'auth/user-mismatch':
            return 'auth.change_password_session_expired';
        // Not a session problem: "sign in again" is advice a disabled account
        // cannot act on.
        case 'auth/user-disabled':
            return 'auth.change_password_account_disabled';
        case NO_FIREBASE_SESSION:
            return 'auth.change_password_no_session';
        case NO_PASSWORD_PROVIDER:
            return 'auth.change_password_no_password_provider';
        default:
            return 'auth.change_password_error';
    }
}

/**
 * Mirrors the API's `MAX_PASSWORD_LENGTH` (`user/service/admin-set-user-password.ts`,
 * `AdminSetUserPasswordInputDto`, `ResetPasswordInputDto`) so the three ways a
 * password can be set on this platform agree. Without it an over-long password
 * fails at Firebase and surfaces as the generic error.
 */
export const MAX_PASSWORD_LENGTH = 1024;

export interface ChangePasswordFormValues {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export interface ChangePasswordFormErrors {
    newPassword?: TranslationKeys;
    confirmPassword?: TranslationKeys;
}

/**
 * Client-side gate for the form. Deliberately mirrors what Firebase would
 * reject anyway (`isValidPassword` is the same rule sign-up uses) plus the two
 * checks Firebase cannot make for us: confirmation match, and "you typed your
 * current password again".
 *
 * Empty fields produce no message — an untouched form should not shout — but
 * they do keep `isValid` false so the submit button stays disabled.
 */
export function validateChangePasswordForm(
    values: ChangePasswordFormValues,
): { errors: ChangePasswordFormErrors; isValid: boolean } {
    const { currentPassword, newPassword, confirmPassword } = values;
    const errors: ChangePasswordFormErrors = {};

    if (newPassword !== '' && !isValidPassword(newPassword)) {
        errors.newPassword = 'auth.change_password_too_short';
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
        errors.newPassword = 'auth.change_password_too_long';
    } else if (newPassword !== '' && newPassword === currentPassword) {
        errors.newPassword = 'auth.change_password_same_as_current';
    }

    if (confirmPassword !== '' && confirmPassword !== newPassword) {
        errors.confirmPassword = 'auth.change_password_mismatch';
    }

    const isValid =
        currentPassword !== '' &&
        newPassword !== '' &&
        confirmPassword !== '' &&
        Object.keys(errors).length === 0;

    return { errors, isValid };
}
